/**
 * CostBasisTab 成本基準 (S9.1) — per-product landed cost build-up table.
 *
 * - Purchase Cost is READ-ONLY on every surface — it always comes from the
 *   purchase document; the edit modal renders it as text and the store patch
 *   type (`CostRecordPatch`) cannot even carry it.
 * - Edits are MANAGER-GATED (`canEditCostBasis`, canDecidePoApproval
 *   precedent): the edit action is HIDDEN for non-managers, never disabled.
 * - Derived numbers (landed / pricing base / list) come from the shared
 *   `buildCostBreakdown` — derive-never-restore, so the table can never show
 *   a stale stored copy.
 * - Y-S2 (yellow-sweep 2026-07-29) — DD!39 Track Landed Cost 追蹤到岸成本
 *   display half: a read-only informational chip over the Y-S0-seeded
 *   `trackLandedCost` / `landedCostAllocationBasis` DATA pair. No allocation
 *   math ships (S3.1 mandate) — the chip records the opt-in, nothing more.
 *
 * S5.2 (plan §S5.2) — read-only FX-rates READ-THROUGH card below the table,
 * labeled 財務記帳與結算-only (Discribe-19 §2: the daily FX table serves
 * accounting display, NEVER product pricing — `ExchangeRate` is not even a
 * `PricingDataset` member, so the resolver cannot see it). `SourceStampTag`
 * carries provenance; a descriptive note REPRESENTS the bulk-update path
 * (CSV import / nightly revaluation script at >5% moves) without
 * implementing it. Unavailable or stale read-throughs render `renderDash`
 * + a stale badge — NEVER zero-as-data (`buildFxCardView` is the exported
 * pure view-model the vitest suite pins; staleness rides the shared
 * `freshnessTierOf` contract).
 *
 * S2.4 — cost-record CREATE, the sealed-legal 來源 write path: 來源 (CH|TW)
 * is chosen HERE at create and ONLY here — after create it is read-only on
 * every surface (`CostRecordPatch` has no `source` member). The Add seat is
 * visible only under `canEditCostBasis()` (hide-don't-disable). NO currency
 * field ships (D27: cost records are AUD like every seed — the store create
 * shape has no currency member, so non-AUD is unrepresentable). Purchase
 * Cost is enterable at create only; the reason gate + 'cost_record' audit
 * row apply exactly as on edit. Demo-mode note: the created record lives in
 * the persisted demo store and evaporates on "Reset demo data" — by design.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  App,
  Button,
  Col,
  DatePicker,
  Dropdown,
  InputNumber,
  Modal,
  Row,
  Segmented,
  Select,
  Skeleton,
  Table,
  Tag,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import type { TableProps } from 'antd';
import type { InputNumberRef } from 'rc-input-number';
import { LockOutlined, MoreOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import dayjs from 'dayjs';
import { useRequest } from 'ahooks';
import TableContainer, { ListEmptyState, renderDash } from '@/components/common/TableContainer';
import ReasonRequiredModal from '@/components/common/ReasonRequiredModal';
import SourceStampTag from '@/components/common/SourceStampTag';
import { useProductOptions } from '@/hooks/useProducts';
import { useVendorOptions } from '@/hooks/useVendors';
import {
  PRICE_SOURCES,
  canEditCostBasis,
  useCostBasisRows,
  usePricingDataset,
  usePricingMutations,
  refreshMinimumMarginPct,
} from '@/hooks/usePricing';
import type { CostBasisRow } from '@/hooks/usePricing';
import {
  buildCostBreakdown,
  buildUpWaterfall,
  effectiveAnchorOf,
  marginToMarkupPct,
  roundHalfUp2,
} from '@/lib/pricing';
import { freshnessTierOf, isStaleTier } from '@/lib/reorder/staleness';
import apiClient from '@/services/api/axios.config';
import { PricingGuardError } from '@/stores/pricingStore';
import type { CostRecordCreate } from '@/stores/pricingStore';
import { useElevation } from '@/styles';
import { useFormStyles } from '@/styles/formStyles';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import type {
  CostBasis,
  CostingMethodDefault,
  ExchangeRate,
  PricingAnchor,
} from '@/types/api/pricing';
import { formatMoney, formatPct } from '../salesOrders/components/soFormat';

const { Text } = Typography;

const pctSchema = (message: string) =>
  z.number({ invalid_type_error: message }).min(-100, message).max(1000, message);

const costFormSchema = z.object({
  landedAllocation: z
    .number({ invalid_type_error: 'pricing.costBasis.modal.validation.allocationInvalid' })
    .min(0, 'pricing.costBasis.modal.validation.allocationInvalid'),
  internalMarkupPct: pctSchema('pricing.costBasis.modal.validation.internalInvalid'),
  salesMarkupPct: pctSchema('pricing.costBasis.modal.validation.salesInvalid'),
  basis: z.enum(['estimated', 'actual']),
  effectiveDate: z.string().min(1, 'pricing.costBasis.modal.validation.effectiveDateRequired'),
});

type CostFormData = z.infer<typeof costFormSchema>;

/**
 * S2.4 create form — identity fields on top of the edit shape. `source` is a
 * required explicit choice (never a silent default: this is the one place
 * 來源 is ever chosen). NO currency member — records are AUD (D27).
 */
// Explicit `: boolean` — an inferred type predicate would narrow the form
// value type to 'CH' | 'TW' and refuse the empty-until-chosen default.
const isPriceSource = (value: string): boolean => value === 'CH' || value === 'TW';

const addCostFormSchema = z.object({
  productId: z.string().min(1, 'pricing.costBasis.addModal.validation.productRequired'),
  source: z
    .string()
    .min(1, 'pricing.costBasis.addModal.validation.sourceRequired')
    .refine(isPriceSource, 'pricing.costBasis.addModal.validation.sourceRequired'),
  basis: z.enum(['estimated', 'actual']),
  purchaseCost: z
    .number({ invalid_type_error: 'pricing.costBasis.addModal.validation.purchaseCostInvalid' })
    .min(0, 'pricing.costBasis.addModal.validation.purchaseCostInvalid'),
  landedAllocation: z
    .number({ invalid_type_error: 'pricing.costBasis.modal.validation.allocationInvalid' })
    .min(0, 'pricing.costBasis.modal.validation.allocationInvalid'),
  internalMarkupPct: pctSchema('pricing.costBasis.modal.validation.internalInvalid'),
  salesMarkupPct: pctSchema('pricing.costBasis.modal.validation.salesInvalid'),
  effectiveDate: z.string().min(1, 'pricing.costBasis.modal.validation.effectiveDateRequired'),
});

type AddCostFormData = z.infer<typeof addCostFormSchema>;

const defaultAddCostValues: AddCostFormData = {
  productId: '',
  source: '',
  basis: 'estimated',
  purchaseCost: 0,
  landedAllocation: 0,
  internalMarkupPct: 0,
  salesMarkupPct: 0,
  effectiveDate: '',
};

const BASIS_COLOR: Record<CostBasis, string> = {
  estimated: 'orange',
  actual: 'green',
};

// ---------------------------------------------------------------------------
// S5.2 — FX read-through view-model (PURE, exported for vitest)
// ---------------------------------------------------------------------------

export const FX_RATES_CACHE_KEY = '/api/pricing/exchange-rates';

export type FxCardView =
  | { status: 'unavailable' }
  | { status: 'fresh' | 'stale'; rows: ExchangeRate[]; syncedAt: string };

/**
 * Classify the FX read-through: no payload ⇒ `unavailable` (the card renders
 * dash placeholders — never a fabricated 0); otherwise the projection stamp
 * is the NEWEST `effectiveDate` and staleness rides the shared
 * `freshnessTierOf` thresholds (a daily accounting table older than the
 * stale threshold renders its rate cells as dashes + a stale badge — an
 * outdated rate shown as a number invites misuse in 財務記帳與結算).
 * Rows are carried VERBATIM (sorted for stable display) — the view never
 * substitutes zeros.
 */
// eslint-disable-next-line react-refresh/only-export-components -- vitest-pinned pure view-model
export function buildFxCardView(
  rates: readonly ExchangeRate[] | null | undefined,
  nowIso: string
): FxCardView {
  if (!rates || rates.length === 0) return { status: 'unavailable' };
  const rows = [...rates].sort(
    (a, b) =>
      a.fromCurrency.localeCompare(b.fromCurrency) || a.toCurrency.localeCompare(b.toCurrency)
  );
  const syncedAt = rates.reduce(
    (max, rate) => (rate.effectiveDate > max ? rate.effectiveDate : max),
    rates[0].effectiveDate
  );
  const stale = isStaleTier(freshnessTierOf(syncedAt, nowIso));
  return { status: stale ? 'stale' : 'fresh', rows, syncedAt };
}

const CostBasisTab = () => {
  const { t } = useTranslation(['pricing', 'common', 'products']);
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const formStyles = useFormStyles();

  const { rows, isLoading, error, refresh } = useCostBasisRows();
  const { dataset } = usePricingDataset();
  const {
    addCostRecord,
    updateCostRecord,
    setPricingAnchorDefault,
    setCostingMethodDefault,
    setMinimumMarginPct,
  } = usePricingMutations();
  const { products } = useProductOptions();
  const managerGate = canEditCostBasis();
  const elevation = useElevation();

  // Minimum margin (2026-09-15) — local draft of the company floor; commits
  // on Enter/blur like the two Segmented defaults above it commit on change.
  // Seeded ABSENT (= the check is off) until a manager types the number; the
  // value is server-authoritative (re-read on every dataset fetch).
  const storedFloor = dataset?.minimumMarginPct ?? null;
  const [floorDraft, setFloorDraft] = useState<number | null>(storedFloor);
  const [floorSaving, setFloorSaving] = useState(false);
  const [floorBlurred, setFloorBlurred] = useState(false);
  // Keyboard: Save/Clear UNMOUNT on success, dropping focus to <body>, and the
  // only confirmation was a toast with no live region — a keyboard or screen
  // reader user pressed Enter and was told nothing (2026-09-16).
  const floorInputRef = useRef<InputNumberRef>(null);
  // The floor is SERVER-authoritative, and the store is persisted: after a
  // reload the card would otherwise show the last locally-known number until
  // the GET lands — a figure the SO handler may not be holding (measured
  // 2026-09-16: a zh frame caught the card showing a cleared floor as set).
  // Nothing renders until the server has answered once this mount; a failed
  // read still releases the gate, so a dead server cannot hide the control.
  const [floorServerRead, setFloorServerRead] = useState(false);
  useEffect(() => {
    let alive = true;
    const release = () => {
      if (alive) setFloorServerRead(true);
    };
    refreshMinimumMarginPct().then(release, release);
    return () => {
      alive = false;
    };
  }, []);
  const [floorSavedNote, setFloorSavedNote] = useState<string | null>(null);
  // TOUCHED-AWARE re-seed (2026-09-16): the draft follows the stored value
  // only while the operator has not typed. Keying the re-seed on dataset
  // identity wiped a half-typed number whenever any overlay landed.
  const floorTouched = useRef(false);
  useEffect(() => {
    if (!floorTouched.current) setFloorDraft(storedFloor);
  }, [storedFloor]);
  const floorDirty = floorDraft !== storedFloor;
  // Judged on the 2dp figure the server will store (round, THEN range-check).
  const floorValid = (value: number) => {
    const rounded = roundHalfUp2(value);
    return Number.isFinite(rounded) && rounded >= 0 && rounded < 100;
  };
  const floorInvalid = floorDraft !== null && !floorValid(floorDraft);
  const releaseFloorDraft = (value: number | null) => {
    floorTouched.current = false;
    setFloorBlurred(false);
    setFloorDraft(value);
  };
  // Explicit commit (Enter or Save) — never on blur: rc-input-number flushes
  // its own clamp on the wrapper AFTER the input's blur, so a blur commit
  // ran on a stale draft and the invalid copy was unreachable (ux-review
  // 2026-09-15). The PATCH is AWAITED and the RESPONSE value is what lands
  // in the store — a toast is never shown before the write is real.
  const commitFloor = async (value: number | null) => {
    if (value !== null && !floorValid(value)) {
      setFloorBlurred(true);
      message.error(t('pricing:pricing.costBasis.minimumMargin.invalid'));
      return;
    }
    const next = value === null ? null : roundHalfUp2(value);
    if (next === storedFloor) {
      releaseFloorDraft(storedFloor);
      return;
    }
    setFloorSaving(true);
    try {
      const landed = await setMinimumMarginPct(next);
      releaseFloorDraft(landed);
      const note = t(
        landed === null
          ? 'pricing:pricing.costBasis.minimumMargin.cleared'
          : 'pricing:pricing.costBasis.minimumMargin.saved'
      );
      message.success(note);
      // Said in the page, not only in a toast, and focus goes back to the
      // field the operator was in rather than to <body>.
      setFloorSavedNote(note);
      floorInputRef.current?.focus();
    } catch {
      releaseFloorDraft(storedFloor);
      setFloorSavedNote(null);
      message.error(t('common:messages.updateFailed'));
      floorInputRef.current?.focus();
    } finally {
      setFloorSaving(false);
    }
  };
  const floorMarkup = storedFloor === null ? null : marginToMarkupPct(storedFloor);

  /** The product universe the picker renders — the store's PRODUCT_NOT_FOUND check. */
  const knownProductIds = useMemo(() => products.map((p) => p.id), [products]);

  // S5.2 FX read-through — GET-only against the S4.1 handler. (A
  // `pricing.api.ts` client wrapper is a recorded follow-up: that file is
  // outside this slice's set, so the thin fetch lives here for now.)
  const {
    data: fxRates,
    error: fxError,
    loading: fxLoading,
    run: refreshFx,
  } = useRequest(
    async () => {
      const { data } = await apiClient.get<{ data: ExchangeRate[] }>('/pricing/exchange-rates');
      return data.data;
    },
    { cacheKey: FX_RATES_CACHE_KEY, staleTime: 60000 }
  );

  const fxView = useMemo<FxCardView>(
    () =>
      fxError ? { status: 'unavailable' } : buildFxCardView(fxRates, new Date().toISOString()),
    [fxRates, fxError]
  );

  const productName = useMemo(() => {
    const byId = new Map(products.map((p) => [p.id, p.displayName]));
    return (id: string) => byId.get(id) ?? id;
  }, [products]);

  // S1.3 display truth — the secondary identity line is the company part
  // number (SKU), never the internal `prod-*` store id (re-measured
  // 2026-08-29: this line was the last raw-id render on any pricing
  // surface). Unresolved lookup (options still loading) falls back to the
  // raw id — an honest identifier beats a blank line.
  const productSku = useMemo(() => {
    const byId = new Map(products.map((p) => [p.id, p.sku]));
    return (id: string) => byId.get(id) ?? id;
  }, [products]);

  // R6 (RULING-10) — linked-vendor name under the source Tag (same join idiom
  // as the product column's useProductOptions). Unlinked records render
  // nothing (dash-free secondary line — absence, not fabricated data).
  const { vendors } = useVendorOptions();
  const vendorName = useMemo(() => {
    const byId = new Map(vendors.map((v) => [v.vendorId, v.vendorName]));
    return (id: number) => byId.get(id) ?? null;
  }, [vendors]);

  const [editingRow, setEditingRow] = useState<CostBasisRow | null>(null);
  /** Validated form values awaiting their mandatory reason (S1.2 audit gate). */
  const [pendingPatch, setPendingPatch] = useState<CostFormData | null>(null);
  /** S2.4 — the add modal + the validated create awaiting its reason. */
  const [addOpen, setAddOpen] = useState(false);
  const [pendingCreate, setPendingCreate] = useState<CostRecordCreate | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm<CostFormData>({
    resolver: zodResolver(costFormSchema),
    defaultValues: {
      landedAllocation: 0,
      internalMarkupPct: 0,
      salesMarkupPct: 0,
      basis: 'estimated',
      effectiveDate: '',
    },
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  const watched = useWatch({ control });

  const {
    control: addControl,
    handleSubmit: handleAddSubmit,
    reset: resetAdd,
    trigger: triggerAdd,
    formState: { errors: addErrors },
  } = useForm<AddCostFormData>({
    resolver: zodResolver(addCostFormSchema),
    defaultValues: defaultAddCostValues,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  const watchedAdd = useWatch({ control: addControl });

  const openAdd = () => {
    resetAdd(defaultAddCostValues);
    setAddOpen(true);
  };

  const closeAdd = () => {
    setAddOpen(false);
    resetAdd(defaultAddCostValues);
  };

  /** Add form OK → build the create, then demand the reason (never save without it). */
  const onAddValid = (values: AddCostFormData) => {
    // Honest narrowing — the schema's refine already guarantees CH | TW.
    if (values.source !== 'CH' && values.source !== 'TW') return;
    setPendingCreate({
      id: `cost-${Date.now()}`,
      productId: values.productId,
      source: values.source,
      basis: values.basis,
      purchaseCost: values.purchaseCost,
      landedAllocation: values.landedAllocation,
      internalMarkupPct: values.internalMarkupPct,
      salesMarkupPct: values.salesMarkupPct,
      effectiveDate: values.effectiveDate,
    });
  };

  const openEdit = (row: CostBasisRow) => {
    setEditingRow(row);
    reset({
      landedAllocation: row.record.landedAllocation,
      internalMarkupPct: row.record.internalMarkupPct,
      salesMarkupPct: row.record.salesMarkupPct,
      basis: row.record.basis,
      effectiveDate: row.record.effectiveDate,
    });
  };

  const closeEdit = () => setEditingRow(null);

  /** Save = stash the validated values; the write happens in the reason gate. */
  const onSave = (values: CostFormData) => {
    if (!editingRow) return;
    setPendingPatch(values);
  };

  /** S1.2/S2.4 — every cost-basis save is audited: reason-gated 'cost_record' rows. */
  const onReasonSubmit = (reason: string) => {
    // S2.4 create path — guards may refuse (nothing written then).
    if (pendingCreate) {
      try {
        addCostRecord(pendingCreate, knownProductIds, reason);
        message.success(t('pricing:pricing.costBasis.messages.added'));
        setPendingCreate(null);
        closeAdd();
      } catch (guardError) {
        message.error(
          guardError instanceof PricingGuardError && guardError.code !== 'REASON_REQUIRED'
            ? t('pricing:pricing.costBasis.messages.addRefused')
            : t('pricing:pricing.costBasis.reasonModal.requiredMessage')
        );
      }
      return;
    }
    if (!editingRow || !pendingPatch) return;
    // Patch type has NO purchaseCost member — read-only by construction.
    updateCostRecord(
      editingRow.record.id,
      {
        landedAllocation: pendingPatch.landedAllocation,
        internalMarkupPct: pendingPatch.internalMarkupPct,
        salesMarkupPct: pendingPatch.salesMarkupPct,
        basis: pendingPatch.basis,
        effectiveDate: pendingPatch.effectiveDate,
      },
      reason
    );
    message.success(t('pricing:pricing.costBasis.messages.saved'));
    setPendingPatch(null);
    closeEdit();
  };

  /** Live derived preview inside the edit modal (same pure chain). */
  /**
   * P3 FIX (2026-08-31) — the preview's CLOSING figure follows the product's
   * Pricing Anchor, because the product page's own build-up does
   * (`buildUpWaterfall` takes the anchor and its `list_price` formula NAMES
   * its multiplicand). Rendering `breakdown.listPrice` here printed the
   * BASE-anchored number under the same List Price label, so the screen that
   * sets a price and the screen that reads it disagreed on any product whose
   * anchor is not `pricing_base`. The chain rows above it are the RECORD's own
   * and stay put — only the list row was ever anchor-dependent.
   */
  const preview = useMemo(() => {
    if (!editingRow) return null;
    const breakdown = buildCostBreakdown({
      ...editingRow.record,
      landedAllocation: watched.landedAllocation ?? editingRow.record.landedAllocation,
      internalMarkupPct: watched.internalMarkupPct ?? editingRow.record.internalMarkupPct,
      salesMarkupPct: watched.salesMarkupPct ?? editingRow.record.salesMarkupPct,
    });
    const anchor = dataset
      ? effectiveAnchorOf(dataset, editingRow.record.productId)
      : 'pricing_base';
    const listRow = buildUpWaterfall(breakdown, anchor).find((row) => row.key === 'list_price');
    return { breakdown, anchor, listRow };
  }, [editingRow, watched, dataset]);

  /** Live derived preview inside the ADD modal — same pure chain, AUD (D27). */
  const addPreview = useMemo(() => {
    if (!addOpen) return null;
    const breakdown = buildCostBreakdown({
      // Identity fields are placeholders — the breakdown math reads only the
      // four amounts + currency (source never changes the arithmetic).
      id: 'cost-preview',
      productId: watchedAdd.productId ?? '',
      source: PRICE_SOURCES.find((source) => source === watchedAdd.source) ?? PRICE_SOURCES[0],
      basis: watchedAdd.basis ?? 'estimated',
      purchaseCost: watchedAdd.purchaseCost ?? 0,
      landedAllocation: watchedAdd.landedAllocation ?? 0,
      internalMarkupPct: watchedAdd.internalMarkupPct ?? 0,
      salesMarkupPct: watchedAdd.salesMarkupPct ?? 0,
      currency: 'AUD',
      fxSnapshot: null,
      effectiveDate: watchedAdd.effectiveDate ?? '',
    });
    // Same P3 fix as `preview`: the closing figure follows the anchor of the
    // product the operator picked in the form. Before a product is chosen
    // there is no anchor to read, and the company default is the honest
    // fallback (it is what the record would resolve on).
    const anchor =
      dataset && watchedAdd.productId
        ? effectiveAnchorOf(dataset, watchedAdd.productId)
        : (dataset?.pricingAnchorDefault ?? 'pricing_base');
    const listRow = buildUpWaterfall(breakdown, anchor).find((row) => row.key === 'list_price');
    return { breakdown, anchor, listRow };
  }, [addOpen, watchedAdd, dataset]);

  const money = (currency: string, value: number) => (
    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatMoney(currency, value)}</span>
  );

  const columns: TableProps<CostBasisRow>['columns'] = [
    {
      title: t('pricing:pricing.costBasis.fields.product'),
      key: 'product',
      width: 220,
      fixed: 'left',
      render: (_: unknown, row) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <span style={{ fontWeight: token.fontWeightStrong }}>
            {productName(row.record.productId)}
          </span>
          <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
            {productSku(row.record.productId)}
          </span>
        </div>
      ),
    },
    {
      title: t('pricing:pricing.costBasis.fields.source'),
      key: 'source',
      width: 180,
      render: (_: unknown, row) => {
        const linkedVendor = row.record.vendorId != null ? vendorName(row.record.vendorId) : null;
        return (
          <div className="tw-flex tw-flex-col tw-gap-0.5">
            <Tag style={{ margin: 0, width: 'fit-content' }}>
              {t(`pricing:pricing.sources.${row.record.source}`)}
            </Tag>
            {linkedVendor && (
              <span
                style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}
                title={t('pricing:pricing.costBasis.linkedVendor')}
                aria-label={t('pricing:pricing.costBasis.linkedVendor')}
              >
                {linkedVendor}
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: t('pricing:pricing.costBasis.fields.basis'),
      key: 'basis',
      width: 100,
      render: (_: unknown, row) => (
        <Tag color={BASIS_COLOR[row.record.basis]} style={{ margin: 0 }}>
          {t(
            `pricing:pricing.fields.${
              row.record.basis === 'estimated' ? 'costBasisEstimated' : 'costBasisActual'
            }`
          )}
        </Tag>
      ),
    },
    {
      title: (
        <Tooltip title={t('pricing:pricing.costBasis.purchaseCostReadOnly')}>
          <span>
            {t('pricing:pricing.costBasis.fields.purchaseCost')}{' '}
            <LockOutlined style={{ fontSize: token.sizeXS, color: token.colorTextTertiary }} />
          </span>
        </Tooltip>
      ),
      key: 'purchaseCost',
      width: 140,
      align: 'right',
      render: (_: unknown, row) => money(row.record.currency, row.record.purchaseCost),
    },
    {
      title: t('pricing:pricing.costBasis.fields.fxSnapshot'),
      key: 'fx',
      width: 190,
      render: (_: unknown, row) =>
        row.record.fxSnapshot ? (
          <div className="tw-flex tw-flex-col tw-gap-0.5">
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
              {row.record.fxSnapshot.fromCurrency} → {row.record.fxSnapshot.toCurrency} ·{' '}
              {row.record.fxSnapshot.rate}
            </span>
            <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
              {dayjs(row.record.fxSnapshot.capturedAt).format('YYYY-MM-DD')}
            </span>
          </div>
        ) : (
          renderDash()
        ),
    },
    {
      title: t('pricing:pricing.costBasis.fields.landedAllocation'),
      key: 'landedAllocation',
      width: 130,
      align: 'right',
      render: (_: unknown, row) => money(row.record.currency, row.record.landedAllocation),
    },
    {
      // DD!39 Track Landed Cost 追蹤到岸成本 (J=SHOULD) — DISPLAY HALF over
      // the Y-S0-seeded opt-in pair (`trackLandedCost` +
      // `landedCostAllocationBasis`, S3.1 "carried as DATA only"). A
      // read-only informational chip: NO allocation math ships with it —
      // `landedAllocation` stays the single amount `buildCostBreakdown`
      // consumes. Un-opted records render the dash, never a fabricated
      // opt-in (zero-vs-empty gate).
      title: t('pricing:pricing.costBasis.fields.trackLandedCost'),
      key: 'trackLandedCost',
      width: 150,
      render: (_: unknown, row) =>
        row.record.trackLandedCost ? (
          <div className="tw-flex tw-flex-col tw-gap-0.5">
            <Tooltip title={t('pricing:pricing.costBasis.trackLandedCostHint')}>
              <Tag style={{ margin: 0, width: 'fit-content' }}>
                {t('pricing:pricing.costBasis.trackLandedCostBadge')}
              </Tag>
            </Tooltip>
            {row.record.landedCostAllocationBasis && (
              <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
                {t(
                  `pricing:pricing.costBasis.allocationBasis.${row.record.landedCostAllocationBasis}`
                )}
              </span>
            )}
          </div>
        ) : (
          renderDash()
        ),
    },
    {
      title: t('pricing:pricing.costBasis.fields.landedCost'),
      key: 'landedCost',
      width: 130,
      align: 'right',
      render: (_: unknown, row) => (
        <span style={{ fontWeight: token.fontWeightStrong, color: token.colorPrimary }}>
          {money(row.record.currency, row.breakdown.landedCost)}
        </span>
      ),
    },
    {
      title: t('pricing:pricing.costBasis.fields.internalMarkupPct'),
      key: 'internalMarkupPct',
      width: 130,
      align: 'right',
      render: (_: unknown, row) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{row.record.internalMarkupPct}%</span>
      ),
    },
    {
      title: t('pricing:pricing.costBasis.fields.pricingBase'),
      key: 'pricingBase',
      width: 130,
      align: 'right',
      render: (_: unknown, row) => money(row.record.currency, row.breakdown.pricingBase),
    },
    {
      title: t('pricing:pricing.costBasis.fields.salesMarkupPct'),
      key: 'salesMarkupPct',
      width: 130,
      align: 'right',
      render: (_: unknown, row) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{row.record.salesMarkupPct}%</span>
      ),
    },
    {
      // P3 FIX (2026-08-31) — the ANCHORED list price, from `row.listPrice`.
      // `row.breakdown.listPrice` is always BASE-anchored, so under any other
      // anchor this column printed a different number from the product page's
      // List Price chip under the same label (measured: prod-fog-cover-01,
      // anchor purchase_cost, 140.00 here vs 127.40 there). One computed
      // figure, one label. The anchor is named beside it so a reader can see
      // WHY two products' list prices are built differently.
      title: t('pricing:pricing.costBasis.fields.listPrice'),
      key: 'listPrice',
      width: 150,
      align: 'right',
      render: (_: unknown, row) => (
        <div className="tw-flex tw-flex-col tw-items-end">
          <span style={{ fontWeight: token.fontWeightStrong }}>
            {money(row.record.currency, row.listPrice)}
          </span>
          <span style={{ fontSize: token.fontSizeSM, color: token.colorTextTertiary }}>
            {t(`pricing:pricing.anchor.${row.anchor}`)}
          </span>
        </div>
      ),
    },
    {
      title: t('pricing:pricing.costBasis.fields.effectiveDate'),
      key: 'effectiveDate',
      width: 120,
      render: (_: unknown, row) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{row.record.effectiveDate}</span>
      ),
    },
    {
      title: t('pricing:pricing.costBasis.fields.actions'),
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_: unknown, row) =>
        // Manager-gated: HIDDEN for other roles (hide-don't-disable)
        managerGate ? (
          <Dropdown
            menu={{
              items: [
                {
                  key: 'edit',
                  label: t('pricing:pricing.costBasis.actions.edit'),
                  onClick: (e) => {
                    e.domEvent.stopPropagation();
                    openEdit(row);
                  },
                },
              ],
            }}
            trigger={['click']}
          >
            <Button
              type="text"
              aria-label={t('pricing:pricing.costBasis.actions.actionsFor', {
                id: row.record.id,
              })}
              icon={<MoreOutlined />}
              onClick={(e) => e.stopPropagation()}
            />
          </Dropdown>
        ) : null,
    },
  ];

  const fieldErrorText = (key?: string) =>
    key ? <Text style={formStyles.errorStyle}>{t(key)}</Text> : null;

  return (
    <>
      <Text
        type="secondary"
        style={{ fontSize: token.fontSizeSM, display: 'block', marginBottom: token.marginMD }}
      >
        {t('pricing:pricing.costBasis.hint')}
      </Text>

      {/* D17 (Melissa 2026-08-27) — company-wide Pricing Anchor default. The
          CONTROL lives here on Price Setup; product surfaces read it back as
          a chip. NO LONGER config-only (Option B, 2026-08-31): the resolver
          multiplies its sell markups by the anchor's figure, and the hint
          below says so. Price bands still read off the Pricing Base (R-BAND),
          which is the other half the hint has to state or an operator will
          expect their bands to move. Manager-gated like every edit on this tab
          (hidden, not disabled, for non-managers).
          OPEN — this control is now price-affecting and is not reason-gated;
          see `setPricingAnchorDefault` in src/stores/pricingStore.ts. */}
      <div
        style={{
          ...elevation.card,
          padding: token.paddingMD,
          borderRadius: token.borderRadiusLG,
          marginBottom: token.marginMD,
        }}
      >
        <Text strong style={{ display: 'block', marginBottom: token.marginXS }}>
          {t('pricing:pricing.costBasis.anchor.title')}
        </Text>
        {/* Honest state: the Segmented renders ONLY when a default exists —
            never a fabricated selection while loading/absent (ux-review). */}
        {dataset?.pricingAnchorDefault ? (
          managerGate ? (
            <Segmented
              value={dataset.pricingAnchorDefault}
              onChange={(value) => setPricingAnchorDefault(value as PricingAnchor)}
              options={(['purchase_cost', 'landed_cost', 'pricing_base'] as const).map(
                (anchor) => ({
                  value: anchor,
                  label: t(`pricing:pricing.anchor.${anchor}`),
                })
              )}
            />
          ) : (
            <Tag style={{ margin: 0 }}>
              {t(`pricing:pricing.anchor.${dataset.pricingAnchorDefault}`)}
            </Tag>
          )
        ) : (
          <span className="tw-inline-flex tw-items-center tw-gap-2">
            {renderDash()}
            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              {t('pricing:pricing.costBasis.anchor.noneSet')}
            </Text>
          </span>
        )}
        <Text
          type="secondary"
          style={{ display: 'block', marginTop: token.marginXS, fontSize: token.fontSizeSM }}
        >
          {t('pricing:pricing.costBasis.anchor.hint')}
        </Text>

        {/* Company costing-method default (Melissa 2026-08-28: "in the price
            setting should be able to select the costing engine … then carry
            it as default"). Same contract as the anchor above: manager-gated
            control, honest absent state, config-only — feeds the COSTING
            engine (COGS/margin), never the sell-price chain. */}
        <Text
          strong
          style={{ display: 'block', marginTop: token.marginMD, marginBottom: token.marginXS }}
        >
          {t('pricing:pricing.costBasis.methodDefault.title')}
        </Text>
        {dataset?.costingMethodDefault ? (
          managerGate ? (
            <Segmented
              value={dataset.costingMethodDefault}
              onChange={(value) => setCostingMethodDefault(value as CostingMethodDefault)}
              options={(
                ['MOVING_AVERAGE', 'FIFO', 'STANDARD', 'WEIGHTED_AVERAGE'] as const
              ).map((method) => ({
                value: method,
                label: t(`products:wizard.costingMethods.${method}`),
              }))}
            />
          ) : (
            <Tag style={{ margin: 0 }}>
              {t(`products:wizard.costingMethods.${dataset.costingMethodDefault}`)}
            </Tag>
          )
        ) : (
          <span className="tw-inline-flex tw-items-center tw-gap-2">
            {renderDash()}
            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              {t('pricing:pricing.costBasis.methodDefault.noneSet')}
            </Text>
          </span>
        )}
        <Text
          type="secondary"
          style={{ display: 'block', marginTop: token.marginXS, fontSize: token.fontSizeSM }}
        >
          {t('pricing:pricing.costBasis.methodDefault.hint')}
        </Text>

        {/* Minimum margin — company default (2026-09-15). Same contract as the
            two defaults above: manager-gated control (hidden, not disabled,
            for non-managers), honest absent state, CONFIG-ONLY — it never
            moves a price; it decides the SO `low_margin` price-review hold.
            Seeded absent: the check is off until a manager types a number. */}
        <Text
          strong
          style={{ display: 'block', marginTop: token.marginMD, marginBottom: token.marginXS }}
        >
          {t('pricing:pricing.costBasis.minimumMargin.title')}
        </Text>
        {/* Loading ≠ absent: no control until the dataset is hydrated — an
            enabled input over an empty store saved to the server but not to
            the store (the measured "90 / check is off" lie). */}
        {!dataset || !floorServerRead ? (
          <Skeleton.Input active size="large" />
        ) : managerGate ? (
          <div className="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
            <InputNumber
              size="large"
              step={0.5}
              precision={2}
              ref={floorInputRef}
              value={floorDraft}
              onChange={(value) => {
                floorTouched.current = true;
                setFloorSavedNote(null);
                setFloorDraft(value === null ? null : Number(value));
              }}
              onBlur={() => setFloorBlurred(true)}
              onPressEnter={() => void commitFloor(floorDraft)}
              addonAfter="%"
              aria-label={t('pricing:pricing.costBasis.minimumMargin.inputLabel')}
              aria-invalid={floorBlurred && floorInvalid}
              aria-errormessage={floorBlurred && floorInvalid ? 'minimum-margin-error' : undefined}
              status={floorBlurred && floorInvalid ? 'error' : undefined}
              style={{ ...formStyles.inputStyle, width: token.controlHeightLG * 5 }}
              disabled={floorSaving}
            />
            {/* Save appears only when there is something to save (hide, don't
                disable); the in-flight disable is the one transient allowed. */}
            {floorDirty && (
              <Button
                type="primary"
                size="large"
                loading={floorSaving}
                onClick={() => void commitFloor(floorDraft)}
              >
                {t('pricing:pricing.costBasis.minimumMargin.save')}
              </Button>
            )}
            {floorMarkup !== null && (
              <Text type="secondary" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {t('pricing:pricing.costBasis.minimumMargin.markupEquivalent', {
                  markup: formatPct(floorMarkup),
                })}
              </Text>
            )}
            {storedFloor !== null && !floorSaving && (
              <Button
                type="link"
                size="small"
                style={{ paddingInline: 0 }}
                onClick={() => {
                  setFloorDraft(null);
                  void commitFloor(null);
                }}
              >
                {t('pricing:pricing.costBasis.minimumMargin.clear')}
              </Button>
            )}
            {/* Inline, at the field, once the operator has left it — the toast
                alone vanished before it could be read (ux-review nit). */}
            {floorBlurred && floorInvalid && (
              <Text
                id="minimum-margin-error"
                type="danger"
                role="alert"
                style={{ flexBasis: '100%', fontSize: token.fontSizeSM }}
              >
                {t('pricing:pricing.costBasis.minimumMargin.invalid')}
              </Text>
            )}
            {/* The live region: a save is announced, not only toasted. */}
            <Text
              type="secondary"
              role="status"
              aria-live="polite"
              style={{ flexBasis: '100%', fontSize: token.fontSizeSM }}
            >
              {floorSavedNote ?? ''}
            </Text>
          </div>
        ) : storedFloor !== null ? (
          <span className="tw-inline-flex tw-items-center tw-gap-2">
            <Tag style={{ margin: 0, fontVariantNumeric: 'tabular-nums' }}>
              {formatPct(storedFloor)}%
            </Tag>
            {floorMarkup !== null && (
              <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                {t('pricing:pricing.costBasis.minimumMargin.markupEquivalent', {
                  markup: formatPct(floorMarkup),
                })}
              </Text>
            )}
          </span>
        ) : (
          <span className="tw-inline-flex tw-items-center tw-gap-2">
            {renderDash()}
            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              {t('pricing:pricing.costBasis.minimumMargin.noneSet')}
            </Text>
          </span>
        )}
        {dataset && managerGate && storedFloor === null && (
          <Text
            type="secondary"
            style={{ display: 'block', marginTop: token.marginXXS, fontSize: token.fontSizeSM }}
          >
            {t('pricing:pricing.costBasis.minimumMargin.noneSet')}
          </Text>
        )}
        <Text
          type="secondary"
          style={{ display: 'block', marginTop: token.marginXS, fontSize: token.fontSizeSM }}
        >
          {t('pricing:pricing.costBasis.minimumMargin.hint')}
        </Text>
        {/* Severity (warn-only vs hold) is an OPEN company decision — the card
            must not present the built behaviour as settled policy. */}
        <Text
          type="secondary"
          style={{ display: 'block', marginTop: token.marginXXS, fontSize: token.fontSizeSM }}
        >
          {t('pricing:pricing.costBasis.minimumMargin.severityNote')}
        </Text>
      </div>

      {/* S2.4 Add seat — manager-visible ONLY (hide-don't-disable). */}
      {managerGate && (
        <div className="tw-mb-4 tw-flex">
          <Button
            type="primary"
            size="large"
            icon={<PlusOutlined />}
            onClick={openAdd}
            style={{
              marginLeft: 'auto',
              borderRadius: radiusTokens.card,
              height: `${controlHeightTokens.controlHeightXL}px`,
            }}
          >
            {t('pricing:pricing.costBasis.addButton')}
          </Button>
        </div>
      )}

      <TableContainer
        errorState={{
          active: !!error && rows.length === 0,
          title: t('pricing:pricing.costBasis.states.loadFailed'),
          retryLabel: t('common:errors.boundary.retry'),
          onRetry: refresh,
        }}
      >
        <Table<CostBasisRow>
          columns={columns}
          dataSource={rows}
          rowKey={(row) => row.record.id}
          loading={isLoading}
          locale={{
            emptyText: <ListEmptyState title={t('pricing:pricing.costBasis.states.noRows')} />,
          }}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          scroll={{ x: 1870 }}
        />
      </TableContainer>

      {/* S5.2 — FX rates READ-THROUGH (財務記帳與結算-only, Discribe-19 §2).
          Display data only: rates never feed product pricing, and the card
          carries zero write affordances. Unavailable/stale ⇒ renderDash +
          stale badge — never zero-as-data. */}
      <div style={{ ...elevation.card, padding: '20px', marginTop: '24px' }}>
        <div className="tw-flex tw-items-start tw-justify-between tw-gap-3 tw-flex-wrap tw-mb-3">
          <div>
            <Text strong style={{ display: 'block' }}>
              {t('pricing:pricing.fx.title')}
            </Text>
            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              {t('pricing:pricing.fx.financeOnlyHint')}
            </Text>
          </div>
          <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
            {fxView.status !== 'unavailable' && (
              <SourceStampTag
                sourceLabel={t('pricing:pricing.fx.sourceLabel')}
                syncedAt={fxView.syncedAt}
              />
            )}
            {fxView.status === 'stale' && (
              <Tag color="orange" style={{ margin: 0 }}>
                {t('pricing:pricing.fx.staleBadge')}
              </Tag>
            )}
          </div>
        </div>

        {fxLoading && fxView.status === 'unavailable' ? (
          <Skeleton active title={false} paragraph={{ rows: 2 }} />
        ) : fxView.status === 'unavailable' ? (
          // Unavailable read-through: dash placeholder + retry — never 0.
          <div className="tw-flex tw-flex-col tw-gap-2">
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{renderDash()}</span>
            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              {t('pricing:pricing.fx.states.unavailable')}
            </Text>
            <Button
              size="large"
              style={{
                height: `${controlHeightTokens.controlHeightXL}px`,
                alignSelf: 'flex-start',
              }}
              onClick={refreshFx}
            >
              {t('common:errors.boundary.retry')}
            </Button>
          </div>
        ) : (
          <TableContainer>
            <Table<ExchangeRate>
              columns={[
                {
                  title: t('pricing:pricing.fx.columns.pair'),
                  key: 'pair',
                  width: 170,
                  render: (_: unknown, rate) => (
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {rate.fromCurrency} → {rate.toCurrency}
                    </span>
                  ),
                },
                {
                  title: t('pricing:pricing.fx.columns.rate'),
                  key: 'rate',
                  align: 'right',
                  width: 140,
                  // A stale daily rate is never shown as a number (and never
                  // as 0) — dash it; the stale badge + effective date say why.
                  render: (_: unknown, rate) =>
                    fxView.status === 'stale' ? (
                      renderDash()
                    ) : (
                      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{rate.rate}</span>
                    ),
                },
                {
                  title: t('pricing:pricing.fx.columns.effectiveDate'),
                  key: 'effectiveDate',
                  width: 130,
                  render: (_: unknown, rate) => (
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{rate.effectiveDate}</span>
                  ),
                },
              ]}
              dataSource={fxView.rows}
              rowKey={(rate) => `${rate.fromCurrency}-${rate.toCurrency}-${rate.effectiveDate}`}
              size="small"
              pagination={false}
            />
          </TableContainer>
        )}

        {/* Bulk-update path REPRESENTED, not implemented (plan §S5.2). */}
        <Text
          type="secondary"
          style={{ display: 'block', marginTop: '12px', fontSize: token.fontSizeSM }}
        >
          {t('pricing:pricing.fx.bulkUpdateNote')}
        </Text>
      </div>

      {/* S2.4 ADD — the sealed-legal 來源 write path (manager seat only).
          Source CH|TW is chosen here at create ONLY; no currency field (D27
          — records are AUD like every seed). */}
      {managerGate && (
        <Modal
          open={addOpen}
          title={t('pricing:pricing.costBasis.addModal.title')}
          onCancel={closeAdd}
          width={640}
          footer={[
            <Button
              key="cancel"
              size="large"
              style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
              onClick={closeAdd}
            >
              {t('common:actions.cancel')}
            </Button>,
            <Button
              key="add"
              type="primary"
              size="large"
              style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
              onClick={() => void handleAddSubmit(onAddValid)()}
            >
              {t('common:actions.add')}
            </Button>,
          ]}
        >
          <div style={{ ...formStyles.fieldContainerStyle, marginBottom: '32px', padding: '20px' }}>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Text style={formStyles.labelStyle}>
                  <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                  {t('pricing:pricing.costBasis.fields.product')}
                </Text>
                <Controller
                  name="productId"
                  control={addControl}
                  render={({ field }) => (
                    <Select
                      value={field.value || undefined}
                      onChange={(value) => {
                        field.onChange(value);
                        triggerAdd('productId');
                      }}
                      onBlur={field.onBlur}
                      size="large"
                      showSearch
                      optionFilterProp="label"
                      placeholder={t('pricing:pricing.costBasis.addModal.productPlaceholder')}
                      options={products.map((product) => ({
                        label: `${product.displayName} (${product.sku})`,
                        value: product.id,
                      }))}
                      status={addErrors.productId ? 'error' : undefined}
                      style={{ ...formStyles.inputStyle, width: '100%' }}
                      aria-label={t('pricing:pricing.costBasis.fields.product')}
                    />
                  )}
                />
                {fieldErrorText(addErrors.productId?.message)}
              </Col>
              <Col span={12}>
                <Text style={formStyles.labelStyle}>
                  <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                  {t('pricing:pricing.costBasis.fields.source')}
                </Text>
                {/* The ONE 來源 choice — explicit, never a silent default;
                    read-only on every surface after create. */}
                <Controller
                  name="source"
                  control={addControl}
                  render={({ field }) => (
                    <Select
                      value={field.value || undefined}
                      onChange={(value) => {
                        field.onChange(value);
                        triggerAdd('source');
                      }}
                      onBlur={field.onBlur}
                      size="large"
                      placeholder={t('pricing:pricing.costBasis.addModal.sourcePlaceholder')}
                      options={PRICE_SOURCES.map((source) => ({
                        label: t(`pricing:pricing.sources.${source}`),
                        value: source,
                      }))}
                      status={addErrors.source ? 'error' : undefined}
                      style={{ ...formStyles.inputStyle, width: '100%' }}
                      aria-label={t('pricing:pricing.costBasis.fields.source')}
                    />
                  )}
                />
                {fieldErrorText(addErrors.source?.message)}
                <Text type="secondary" style={{ fontSize: token.fontSizeSM, display: 'block' }}>
                  {t('pricing:pricing.costBasis.addModal.sourceCreateOnlyHint')}
                </Text>
              </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginTop: '12px' }}>
              <Col span={12}>
                <Text style={formStyles.labelStyle}>
                  {t('pricing:pricing.costBasis.modal.basisLabel')}
                </Text>
                <Controller
                  name="basis"
                  control={addControl}
                  render={({ field }) => (
                    <Segmented
                      value={field.value}
                      onChange={(value) => field.onChange(value as CostBasis)}
                      size="large"
                      options={[
                        {
                          label: t('pricing:pricing.fields.costBasisEstimated'),
                          value: 'estimated',
                        },
                        { label: t('pricing:pricing.fields.costBasisActual'), value: 'actual' },
                      ]}
                    />
                  )}
                />
              </Col>
              <Col span={12}>
                <Text style={formStyles.labelStyle}>
                  <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                  {t('pricing:pricing.costBasis.fields.effectiveDate')}
                </Text>
                <Controller
                  name="effectiveDate"
                  control={addControl}
                  render={({ field }) => (
                    <DatePicker
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(value) => {
                        field.onChange(value ? value.format('YYYY-MM-DD') : '');
                        triggerAdd('effectiveDate');
                      }}
                      size="large"
                      style={{ ...formStyles.inputStyle, width: '100%' }}
                      status={addErrors.effectiveDate ? 'error' : undefined}
                      aria-label={t('pricing:pricing.costBasis.fields.effectiveDate')}
                    />
                  )}
                />
                {fieldErrorText(addErrors.effectiveDate?.message)}
              </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginTop: '12px' }}>
              <Col span={12}>
                <Text style={formStyles.labelStyle}>
                  <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                  {t('pricing:pricing.costBasis.fields.purchaseCost')}
                </Text>
                {/* Enterable at CREATE only — read-only everywhere after
                    (CostRecordPatch has no purchaseCost member). */}
                <Controller
                  name="purchaseCost"
                  control={addControl}
                  render={({ field }) => (
                    <InputNumber
                      value={field.value}
                      onChange={(value) => field.onChange(value ?? 0)}
                      onBlur={field.onBlur}
                      size="large"
                      min={0}
                      style={{ ...formStyles.inputStyle, width: '100%' }}
                      status={addErrors.purchaseCost ? 'error' : undefined}
                      aria-label={t('pricing:pricing.costBasis.fields.purchaseCost')}
                    />
                  )}
                />
                {fieldErrorText(addErrors.purchaseCost?.message)}
                <Text type="secondary" style={{ fontSize: token.fontSizeSM, display: 'block' }}>
                  {t('pricing:pricing.costBasis.addModal.currencyNote')}
                </Text>
              </Col>
              <Col span={12}>
                <Text style={formStyles.labelStyle}>
                  <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                  {t('pricing:pricing.costBasis.fields.landedAllocation')}
                </Text>
                <Controller
                  name="landedAllocation"
                  control={addControl}
                  render={({ field }) => (
                    <InputNumber
                      value={field.value}
                      onChange={(value) => field.onChange(value ?? 0)}
                      onBlur={field.onBlur}
                      size="large"
                      min={0}
                      style={{ ...formStyles.inputStyle, width: '100%' }}
                      status={addErrors.landedAllocation ? 'error' : undefined}
                      aria-label={t('pricing:pricing.costBasis.fields.landedAllocation')}
                    />
                  )}
                />
                {fieldErrorText(addErrors.landedAllocation?.message)}
              </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginTop: '12px' }}>
              <Col span={12}>
                <Text style={formStyles.labelStyle}>
                  <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                  {t('pricing:pricing.costBasis.fields.internalMarkupPct')}
                </Text>
                <Controller
                  name="internalMarkupPct"
                  control={addControl}
                  render={({ field }) => (
                    <InputNumber
                      value={field.value}
                      onChange={(value) => field.onChange(value ?? 0)}
                      onBlur={field.onBlur}
                      size="large"
                      suffix="%"
                      style={{ ...formStyles.inputStyle, width: '100%' }}
                      status={addErrors.internalMarkupPct ? 'error' : undefined}
                      aria-label={t('pricing:pricing.costBasis.fields.internalMarkupPct')}
                    />
                  )}
                />
                {fieldErrorText(addErrors.internalMarkupPct?.message)}
              </Col>
              <Col span={12}>
                <Text style={formStyles.labelStyle}>
                  <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                  {t('pricing:pricing.costBasis.fields.salesMarkupPct')}
                </Text>
                <Controller
                  name="salesMarkupPct"
                  control={addControl}
                  render={({ field }) => (
                    <InputNumber
                      value={field.value}
                      onChange={(value) => field.onChange(value ?? 0)}
                      onBlur={field.onBlur}
                      size="large"
                      suffix="%"
                      style={{ ...formStyles.inputStyle, width: '100%' }}
                      status={addErrors.salesMarkupPct ? 'error' : undefined}
                      aria-label={t('pricing:pricing.costBasis.fields.salesMarkupPct')}
                    />
                  )}
                />
                {fieldErrorText(addErrors.salesMarkupPct?.message)}
              </Col>
            </Row>
          </div>

          {/* Live derived build-up — same pure chain the table renders */}
          {addPreview && (
            <div style={{ ...formStyles.fieldContainerStyle, padding: '20px' }}>
              <Text strong style={formStyles.sectionTitleStyle}>
                {t('pricing:pricing.costBasis.modal.derivedPreview')}
              </Text>
              <Text style={{ display: 'block', fontVariantNumeric: 'tabular-nums' }}>
                {t('pricing:pricing.waterfall.landedCost')}{' '}
                {addPreview.breakdown.landedCost.toFixed(2)} × (1 +{' '}
                {addPreview.breakdown.internalMarkupPct}%) →{' '}
                {t('pricing:pricing.waterfall.pricingBase')}{' '}
                {addPreview.breakdown.pricingBase.toFixed(2)} →{' '}
                {t('pricing:pricing.waterfall.listPrice')} {addPreview.listRow?.formula} ={' '}
                {addPreview.listRow?.amount.toFixed(2)} ({t(`pricing:pricing.anchor.${addPreview.anchor}`)})
              </Text>
            </div>
          )}
        </Modal>
      )}

      {/* Manager edit modal — Purchase Cost rendered as TEXT, never an input */}
      <Modal
        open={!!editingRow}
        title={t('pricing:pricing.costBasis.modal.title')}
        onCancel={closeEdit}
        width={640}
        footer={[
          <Button
            key="cancel"
            size="large"
            style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
            onClick={closeEdit}
          >
            {t('common:actions.cancel')}
          </Button>,
          <Button
            key="save"
            type="primary"
            size="large"
            style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
            loading={isSubmitting}
            onClick={() => void handleSubmit(onSave)()}
          >
            {t('common:actions.save')}
          </Button>,
        ]}
      >
        {editingRow && (
          <>
            <Text type="secondary" style={{ display: 'block', marginBottom: token.marginMD }}>
              {productName(editingRow.record.productId)} ·{' '}
              {t(`pricing:pricing.sources.${editingRow.record.source}`)}
            </Text>

            <div style={{ ...formStyles.fieldContainerStyle, marginBottom: '32px', padding: '20px' }}>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Text style={formStyles.labelStyle}>
                    {t('pricing:pricing.costBasis.fields.purchaseCost')}{' '}
                    <LockOutlined
                      style={{ fontSize: token.sizeXS, color: token.colorTextTertiary }}
                    />
                  </Text>
                  {/* READ-ONLY — never overridable, so never an input */}
                  <Text strong style={{ display: 'block', fontVariantNumeric: 'tabular-nums' }}>
                    {formatMoney(editingRow.record.currency, editingRow.record.purchaseCost)}
                  </Text>
                  <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                    {t('pricing:pricing.costBasis.purchaseCostReadOnly')}
                  </Text>
                </Col>
                <Col span={12}>
                  <Text style={formStyles.labelStyle}>
                    <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                    {t('pricing:pricing.costBasis.fields.landedAllocation')}
                  </Text>
                  <Controller
                    name="landedAllocation"
                    control={control}
                    render={({ field }) => (
                      <InputNumber
                        value={field.value}
                        onChange={(value) => field.onChange(value ?? 0)}
                        onBlur={field.onBlur}
                        size="large"
                        min={0}
                        style={{ ...formStyles.inputStyle, width: '100%' }}
                        status={errors.landedAllocation ? 'error' : undefined}
                        aria-label={t('pricing:pricing.costBasis.fields.landedAllocation')}
                      />
                    )}
                  />
                  {fieldErrorText(errors.landedAllocation?.message)}
                </Col>
              </Row>
              <Row gutter={[16, 16]} style={{ marginTop: '12px' }}>
                <Col span={12}>
                  <Text style={formStyles.labelStyle}>
                    <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                    {t('pricing:pricing.costBasis.fields.internalMarkupPct')}
                  </Text>
                  <Controller
                    name="internalMarkupPct"
                    control={control}
                    render={({ field }) => (
                      <InputNumber
                        value={field.value}
                        onChange={(value) => field.onChange(value ?? 0)}
                        onBlur={field.onBlur}
                        size="large"
                        suffix="%"
                        style={{ ...formStyles.inputStyle, width: '100%' }}
                        status={errors.internalMarkupPct ? 'error' : undefined}
                        aria-label={t('pricing:pricing.costBasis.fields.internalMarkupPct')}
                      />
                    )}
                  />
                  {fieldErrorText(errors.internalMarkupPct?.message)}
                </Col>
                <Col span={12}>
                  <Text style={formStyles.labelStyle}>
                    <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                    {t('pricing:pricing.costBasis.fields.salesMarkupPct')}
                  </Text>
                  <Controller
                    name="salesMarkupPct"
                    control={control}
                    render={({ field }) => (
                      <InputNumber
                        value={field.value}
                        onChange={(value) => field.onChange(value ?? 0)}
                        onBlur={field.onBlur}
                        size="large"
                        suffix="%"
                        style={{ ...formStyles.inputStyle, width: '100%' }}
                        status={errors.salesMarkupPct ? 'error' : undefined}
                        aria-label={t('pricing:pricing.costBasis.fields.salesMarkupPct')}
                      />
                    )}
                  />
                  {fieldErrorText(errors.salesMarkupPct?.message)}
                </Col>
              </Row>
              <Row gutter={[16, 16]} style={{ marginTop: '12px' }}>
                <Col span={12}>
                  <Text style={formStyles.labelStyle}>
                    {t('pricing:pricing.costBasis.modal.basisLabel')}
                  </Text>
                  <Controller
                    name="basis"
                    control={control}
                    render={({ field }) => (
                      <Segmented
                        value={field.value}
                        onChange={(value) => field.onChange(value as CostBasis)}
                        size="large"
                        options={[
                          {
                            label: t('pricing:pricing.fields.costBasisEstimated'),
                            value: 'estimated',
                          },
                          { label: t('pricing:pricing.fields.costBasisActual'), value: 'actual' },
                        ]}
                      />
                    )}
                  />
                </Col>
                <Col span={12}>
                  <Text style={formStyles.labelStyle}>
                    <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                    {t('pricing:pricing.costBasis.fields.effectiveDate')}
                  </Text>
                  <Controller
                    name="effectiveDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        value={field.value ? dayjs(field.value) : null}
                        onChange={(value) => {
                          field.onChange(value ? value.format('YYYY-MM-DD') : '');
                          trigger('effectiveDate');
                        }}
                        size="large"
                        style={{ ...formStyles.inputStyle, width: '100%' }}
                        status={errors.effectiveDate ? 'error' : undefined}
                        aria-label={t('pricing:pricing.costBasis.fields.effectiveDate')}
                      />
                    )}
                  />
                  {fieldErrorText(errors.effectiveDate?.message)}
                </Col>
              </Row>
            </div>

            {/* Live derived build-up — same pure chain as the table */}
            {preview && (
              <div style={{ ...formStyles.fieldContainerStyle, padding: '20px' }}>
                <Text strong style={formStyles.sectionTitleStyle}>
                  {t('pricing:pricing.costBasis.modal.derivedPreview')}
                </Text>
                <Text style={{ display: 'block', fontVariantNumeric: 'tabular-nums' }}>
                  {t('pricing:pricing.waterfall.landedCost')}{' '}
                  {preview.breakdown.landedCost.toFixed(2)} × (1 +{' '}
                  {preview.breakdown.internalMarkupPct}%) →{' '}
                  {t('pricing:pricing.waterfall.pricingBase')}{' '}
                  {preview.breakdown.pricingBase.toFixed(2)} →{' '}
                  {t('pricing:pricing.waterfall.listPrice')} {preview.listRow?.formula} ={' '}
                  {preview.listRow?.amount.toFixed(2)} ({t(`pricing:pricing.anchor.${preview.anchor}`)})
                </Text>
              </div>
            )}
          </>
        )}
      </Modal>

      {/* The ONE reason gate (S1.2/S2.4) — cost-basis edits AND creates land
          in the same PRICE_OVERRIDE review lens as every other
          price-affecting save (entityType 'cost_record'). */}
      <ReasonRequiredModal
        open={pendingPatch !== null || pendingCreate !== null}
        title={t('pricing:pricing.costBasis.reasonModal.title')}
        description={t('pricing:pricing.costBasis.reasonModal.description')}
        submitText={t('pricing:pricing.costBasis.reasonModal.submit')}
        cancelText={t('common:actions.cancel')}
        reasonLabel={t('pricing:pricing.costBasis.reasonModal.reasonLabel')}
        reasonPlaceholder={t('pricing:pricing.costBasis.reasonModal.reasonPlaceholder')}
        requiredMessage={t('pricing:pricing.costBasis.reasonModal.requiredMessage')}
        onSubmit={onReasonSubmit}
        onCancel={() => {
          setPendingPatch(null);
          setPendingCreate(null);
        }}
      />
    </>
  );
};

export default CostBasisTab;
